import { Box, Button, CircularProgress, Dialog, Divider, IconButton, Theme, Typography, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { RefObject, useState } from "react";
import { FilterValues } from "../index";
import { FilePond, registerPlugin } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import { CONTENT_TYPE } from "../../../../utils/constant";
import { useSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import { downloadHoldingsData, getSecuritiesPreview, updateExcludedSecurityList } from "../../../../redux-store/actions/reports";
import { GetSecuritiesPreviewResponseBody, UpdateExcludedSecurityListResponseBody } from "../../../../redux-store/types/onBoarding";
import { reportDownload } from "../../../Reports/report-utils";
import SecurityPreviewTable from "./SecurityPreviewTable";
import StatusPopup from "../../StatusPopup";

registerPlugin(FilePondPluginFileValidateType, FilePondPluginFileValidateSize);

interface AddSecuritiesExclusionsPopupProps {
    open: boolean;
    onClose: () => void;
    lastAppliedFilterValuesRef: RefObject<FilterValues | null>;
    triggerExcludedSecuritiesApi: () => void;
}

export default function AddSecuritiesExclusionsPopup({
    open,
    onClose,
    lastAppliedFilterValuesRef,
    triggerExcludedSecuritiesApi
}: AddSecuritiesExclusionsPopupProps): JSX.Element {
    const theme = useTheme();
    const dispatch = useDispatch()

    const { enqueueSnackbar } = useSnackbar();

    const [files, setFiles] = useState<any[]>([]);
    const [loadingStatus, setLoadingStatus] = useState<
        "downloadingFile" |
        "gettingDataPreview" |
        "savingData" |
        ""
    >("");
    const [uploadError, setUploadError] = useState<string>("");
    const [securitiesPreviewData, setSecuritiesPreviewData] = useState<GetSecuritiesPreviewResponseBody | null>(null);
    const [currentStep, setCurrentStep] = useState<string>("1");
    const [updateStatusPopupData, setUpdateStatusPopupData] = useState<UpdateExcludedSecurityListResponseBody & { isOpen: boolean }>({
        isOpen: false,
        isSuccess: false,
        message: "",
        body: ""
    })

    const handleClose = () => {
        setFiles([]);
        setLoadingStatus("");
        setUploadError("");
        setSecuritiesPreviewData(null);
        setCurrentStep("1");
        setUpdateStatusPopupData({
            isOpen: false,
            isSuccess: false,
            message: "",
            body: ""
        })

        onClose();
    };

    const handleBack = () => {
        setLoadingStatus("");
        setUploadError("");
        setSecuritiesPreviewData(null);
        setCurrentStep("1");
        setUpdateStatusPopupData({
            isOpen: false,
            isSuccess: false,
            message: "",
            body: ""
        });
    }

    const handleDownload = async () => {
        try {
            if (!lastAppliedFilterValuesRef.current) {
                return
            }

            const values = lastAppliedFilterValuesRef.current

            setLoadingStatus("downloadingFile");

            const payload = {
                year: Number(values.year),
                quarter: values.quarter,
                portfolioIds: [values?.individual?.clientCode ?? ""]
            }

            const response = await (dispatch(downloadHoldingsData(payload)) as unknown as Promise<any>)

            if (response && [CONTENT_TYPE.EXCEL].includes(response.type)) {
                enqueueSnackbar('File downloaded successfully.', {
                    variant: 'success',
                    autoHideDuration: 2000,
                });
                reportDownload(response as unknown as Blob, 'HoldingsData.xlsx');
            } else {
                enqueueSnackbar('File download failed!.', {
                    variant: 'error',
                    autoHideDuration: 2000,
                });
            }
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setLoadingStatus("")
        }
    }

    const handlePreview = async () => {
        try {
            if (!(files ?? []).length) {
                setUploadError("Please upload a file");
                return;
            }

            setLoadingStatus("gettingDataPreview");

            const formData = new FormData();
            formData.append("file", files[0].file);

            const response = await (dispatch(getSecuritiesPreview(formData)) as unknown as Promise<GetSecuritiesPreviewResponseBody>);

            if (response) {
                setSecuritiesPreviewData(response);
                setCurrentStep("2")
            }
        } catch (error) {
            console.log("Error: ", error);
        } finally {
            setLoadingStatus("");
        }
    }

    const handleSave = async () => {
        try {
            if (!lastAppliedFilterValuesRef?.current) {
                return
            }

            setLoadingStatus("savingData")

            const values = lastAppliedFilterValuesRef.current

            const payload = {
                year: Number(values.year),
                quarter: values.quarter,
                securityList: (securitiesPreviewData?.data ?? []).map(obj => ({
                    portfolio: obj?.portfolio ?? "",
                    name: obj?.name ?? "",
                    security: obj?.security ?? "",
                    securityName: obj?.securityName ?? "",
                    isExcluded: obj?.isExcluded || "N",
                    exclusionReason: obj?.exclusionReason ?? "",
                    familyId: obj?.familyId ?? "",
                    familyName: obj?.familyName ?? "",
                    clientCode: obj?.clientCode ?? ""
                }))
            }

            const response = await (dispatch(updateExcludedSecurityList(payload)) as unknown as Promise<UpdateExcludedSecurityListResponseBody>);

            if (response) {
                setUpdateStatusPopupData({
                    isOpen: true,
                    isSuccess: response?.isSuccess,
                    message: response?.message ?? "",
                    body: response?.body ?? ""
                })

                if (response?.isSuccess) {
                    triggerExcludedSecuritiesApi()
                    handleClose()
                }
            }
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setLoadingStatus("")
        }
    }

    const handleUpdateStatusPopupClose = () => {
        setUpdateStatusPopupData((prev) => ({
            ...prev,
            isOpen: false,
        }))
    }

    return (
        <>
            <Dialog
                open={open}
                maxWidth={currentStep === "1" ? "sm" : "md"}
                fullWidth
                sx={(theme: Theme) => ({
                    "& .MuiPaper-root": {
                        borderRadius: "25px",
                        backgroundColor: theme.palette.common.white,
                        padding: { xs: "20px 15px", sm: "30px" },
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                    },
                })}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: "5px"
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={{ color: theme.palette.text.primary }}
                    >
                        {currentStep === "1" ? "Add Securities Exclusions" : "Preview Uploaded Securities"}
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        size="small"
                        disabled={["downloadingFile", "gettingDataPreview", "savingData"].includes(loadingStatus)}
                        sx={{
                            "&.Mui-disabled": {
                                cursor: "not-allowed",
                                pointerEvents: "auto",
                            },
                        }}
                    >
                        <CloseIcon
                            sx={{
                                color: ["downloadingFile", "gettingDataPreview"].includes(loadingStatus)
                                    ? theme.palette.grey[400]
                                    : theme.palette.text.primary,
                            }}
                        />
                    </IconButton>
                </Box>
                {(currentStep === "1") && (
                    <Typography
                        variant="subtitle_500"
                        sx={{
                            color: theme.palette.textDark.secondary,
                            mb: "15px"
                        }}
                    >
                        Follow the steps below to upload your holdings file and apply exclusions.
                    </Typography>
                )}
                <Divider
                    sx={{
                        borderColor: theme.palette.grey[500],
                        mb: "30px"
                    }}
                />
                {(currentStep === "1") && (
                    <>
                        <Box sx={{ mb: "24px" }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                    mb: "12px"
                                }}
                            >
                                <Box
                                    sx={{
                                        backgroundColor: theme.palette.primary[50],
                                        borderRadius: "20px",
                                        padding: "4px 14px"
                                    }}
                                >
                                    <Typography
                                        variant="subtitle_500"
                                        sx={{
                                            color: theme.palette.primary[500],
                                            fontWeight: 600
                                        }}
                                    >
                                        Step 1
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="body_500"
                                    sx={{
                                        color: theme.palette.text.primary,
                                        fontWeight: 600
                                    }}
                                >
                                    Download Holdings File
                                </Typography>
                            </Box>
                            <Typography
                                variant="subtitle_500"
                                sx={{
                                    color: theme.palette.textDark.secondary,
                                    mb: "15px"
                                }}
                            >
                                Download the current Holdings file to use as a template.
                            </Typography>
                            <Button
                                variant="outlined"
                                startIcon={
                                    ["downloadingFile"].includes(loadingStatus)
                                        ? <CircularProgress size={18} sx={{ color: theme.palette.primary[500] }} />
                                        : <FileDownloadOutlinedIcon />
                                }
                                onClick={handleDownload}
                                disabled={["downloadingFile", "gettingDataPreview"].includes(loadingStatus)}
                                fullWidth
                                sx={{
                                    ...theme.typography.body_500,
                                    color: theme.palette.primary[500],
                                    borderColor: theme.palette.primary[500],
                                    borderRadius: "10px",
                                    fontWeight: 600,
                                    py: "10px",
                                    "&:hover": {
                                        backgroundColor: `${theme.palette.primary[500]}0A`,
                                        borderColor: theme.palette.primary[500],
                                    }
                                }}
                            >
                                Holdings File
                            </Button>
                        </Box>
                        <Divider
                            sx={{
                                borderColor: theme.palette.grey[500],
                                mb: "24px"
                            }}
                        />
                        <Box sx={{ mb: "24px" }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                    mb: "15px"
                                }}
                            >
                                <Box
                                    sx={{
                                        backgroundColor: theme.palette.primary[50],
                                        borderRadius: "20px",
                                        padding: "4px 14px"
                                    }}
                                >
                                    <Typography
                                        variant="subtitle_500"
                                        sx={{
                                            color: theme.palette.primary[500],
                                            fontWeight: 600
                                        }}
                                    >
                                        Step 2
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="body_500"
                                    sx={{
                                        color: theme.palette.text.primary,
                                        fontWeight: 600
                                    }}
                                >
                                    Upload Holdings File
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    '.filepond--root': {
                                        fontFamily: theme.typography.fontFamily,
                                        border: `2px dashed ${theme.palette.grey[400]}`,
                                        borderRadius: '15px',
                                        padding: '30px 20px',
                                        minHeight: '150px',
                                        marginBottom: 0,
                                        ...(["downloadingFile", "gettingDataPreview"].includes(loadingStatus) && {
                                            cursor: 'not-allowed',
                                        }),
                                    },
                                    '.filepond--panel-root': { backgroundColor: 'transparent' },
                                    '.filepond--credits': { display: 'none' },
                                    '.filepond--drop-label': {
                                        cursor: ["downloadingFile", "gettingDataPreview"].includes(loadingStatus) ? 'not-allowed' : 'pointer',
                                        pointerEvents: 'all',
                                        minHeight: '120px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    },
                                    '.filepond--label-action': {
                                        ...theme.typography.body_500,
                                        color: theme.palette.text.primary,
                                        fontFamily: theme.typography.fontFamily,
                                        fontWeight: 700,
                                        textDecoration: 'none',
                                        cursor: ["downloadingFile", "gettingDataPreview"].includes(loadingStatus) ? 'not-allowed' : 'pointer',
                                    },
                                    '.filepond-upload-icon': {
                                        width: '50px',
                                        height: '50px',
                                        borderRadius: '50%',
                                        backgroundColor: `${theme.palette.primary[500]}15`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 10px',
                                    },
                                    '.filepond-upload-icon svg': {
                                        width: '24px',
                                        height: '24px',
                                        fill: theme.palette.primary[500],
                                    },
                                    '.filepond-supported-formats': {
                                        ...theme.typography.subtitle_500,
                                        color: theme.palette.textDark.secondary,
                                        fontWeight: 400,
                                        fontFamily: theme.typography.fontFamily,
                                        marginTop: '4px',
                                    },
                                    '.filepond--file': { color: theme.palette.text.primary, fontWeight: 600 },
                                    '.filepond--file-wrapper': { backgroundColor: `${theme.palette.primary[500]}15` },
                                    "[data-filepond-item-state*='processing-complete'] .filepond--item-panel": {
                                        backgroundColor: `${theme.palette.primary[500]}15`,
                                    },
                                    ...(!["downloadingFile", "gettingDataPreview"].includes(loadingStatus) && {
                                        '.filepond--root:hover': {
                                            borderColor: theme.palette.primary[500],
                                            backgroundColor: `${theme.palette.primary[500]}05`,
                                        },
                                    }),
                                }}
                            >
                                <FilePond
                                    name="file"
                                    files={files}
                                    onupdatefiles={(fileItems) => {
                                        setFiles(fileItems);
                                        setUploadError("");
                                    }}
                                    allowMultiple={true}
                                    allowPaste={false}
                                    maxFiles={1}
                                    onwarning={(error) => {
                                        if (error.body === 'Max files') {
                                            setUploadError("You can only upload 1 file at a time.");
                                        }
                                    }}
                                    acceptedFileTypes={[CONTENT_TYPE.EXCEL]}
                                    labelIdle={`
                            <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;">
                                <div class="filepond-upload-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                        <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/>
                                    </svg>
                                </div>
                                <span class="filepond--label-action">Drag & Drop</span>
                                <div class="filepond-supported-formats">Supported formats: XLSX</div>
                            </div>
                        `}
                                    labelFileTypeNotAllowed="Only XLSX files are supported"
                                    fileValidateTypeLabelExpectedTypesMap={{ [CONTENT_TYPE.EXCEL]: '.xlsx' }}
                                    fileValidateTypeLabelExpectedTypes="Expects {lastType}"
                                    disabled={["downloadingFile", "gettingDataPreview"].includes(loadingStatus)}
                                />
                            </Box>
                            {uploadError && (
                                <Typography
                                    variant="subtitle_500"
                                    sx={{
                                        color: theme.palette.error[500],
                                        mt: "8px",
                                    }}
                                >
                                    {uploadError}
                                </Typography>
                            )}
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                borderRadius: "10px",
                                padding: "12px 16px",
                                mb: "35px",
                                border: `1px solid ${theme.palette.warning[500]}`,
                            }}
                        >
                            <WarningAmberIcon
                                sx={{
                                    ...theme.typography.subtitle_500,
                                    color: theme.palette.warning[500],
                                    flexShrink: 0
                                }}
                            />
                            <Typography
                                variant="subtitle_500"
                                sx={{
                                    color: theme.palette.text.primary,
                                    fontWeight: 600
                                }}
                            >
                                Note: Uploading a new file will replace all existing exclusions.
                            </Typography>
                        </Box>
                    </>
                )}
                {(currentStep === '2') && (
                    <Box
                        sx={{
                            maxHeight: "600px",
                            overflowY: "auto",
                            mb: "20px",
                        }}
                    >
                        <SecurityPreviewTable securitiesPreviewData={securitiesPreviewData} />
                    </Box>
                )}
                <Box
                    sx={{
                        display: "flex",
                        gap: "16px"
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={(currentStep === "1") ? handleClose : handleBack}
                        fullWidth
                        sx={{
                            ...theme.typography.body_500,
                            color: theme.palette.text.primary,
                            borderColor: theme.palette.grey[400],
                            borderRadius: "10px",
                            fontWeight: 600,
                            py: "10px",
                        }}
                        disabled={["downloadingFile", "gettingDataPreview", "savingData"].includes(loadingStatus)}
                    >
                        {(currentStep === "1") ? "Cancel" : "Back"}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={(currentStep === "1") ? handlePreview : handleSave}
                        fullWidth
                        startIcon={
                            ["gettingDataPreview", "savingData"].includes(loadingStatus)
                                ? <CircularProgress size={18} sx={{ color: theme.palette.primary[500] }} />
                                : undefined
                        }
                        sx={{
                            ...theme.typography.body_500,
                            backgroundColor: theme.palette.primary[500],
                            borderRadius: "10px",
                            fontWeight: 600,
                            py: "10px",
                            color: theme.palette.text.primary,
                            "&.Mui-disabled": {
                                backgroundColor: `${theme.palette.primary[500]}80`,
                                color: theme.palette.common.white
                            },
                        }}
                        disabled={["downloadingFile", "gettingDataPreview", "savingData"].includes(loadingStatus)}
                    >
                        {(currentStep === "1") ? "Preview" : "Save"}
                    </Button>
                </Box>
            </Dialog>
            <StatusPopup
                open={updateStatusPopupData.isOpen}
                isSuccess={updateStatusPopupData.isSuccess}
                message={updateStatusPopupData.message}
                buttonText={updateStatusPopupData.isSuccess ? "Done" : "Cancel"}
                onButtonClick={handleUpdateStatusPopupClose}
            />
        </>
    );
}
